import React, { useState, useEffect } from 'react';
import { Save, X, Plus, Trash2 } from 'lucide-react';
import { createMenu, updateMenu, addOpcion } from '../../api/cafeteria';

const EMPTY_OP = {
  numero_opcion: 1,
  principal: '', proteina: '', acompanamiento1: '', acompanamiento2: '',
  ensalada: '', bebida: '', postre: '', precio: '', cupos_maximos: ''
};

const CAMPOS_OP = [
  { key: 'principal',      label: '🍚 Plato Principal',   ph: 'Ej: Arroz blanco',       required: true },
  { key: 'proteina',       label: '🍗 Proteína',          ph: 'Ej: Pollo guisado' },
  { key: 'acompanamiento1',label: '🫘 Acompañamiento 1',  ph: 'Ej: Habichuelas rojas' },
  { key: 'acompanamiento2',label: '🥔 Acompañamiento 2',  ph: 'Ej: Tostones' },
  { key: 'ensalada',       label: '🥬 Ensalada',          ph: 'Ej: Ensalada verde' },
  { key: 'bebida',         label: '🥤 Bebida',            ph: 'Ej: Jugo de chinola' },
  { key: 'postre',         label: '🍰 Postre',            ph: 'Ej: Flan de coco (opcional)' },
];

export default function MenuForm({ menu, onSaved, onCancel }) {
  const isEditing = !!menu;

  const [form, setForm] = useState({ fecha: '', hora_limite: '12:00', descripcion: '' });
  const [opciones, setOpciones] = useState([]);
  const [opForm, setOpForm] = useState(EMPTY_OP);
  const [showOpForm, setShowOpForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (menu) {
      setForm({
        fecha: menu.fecha?.split('T')[0] || '',
        hora_limite: menu.hora_limite?.slice(0, 5) || '12:00',
        descripcion: menu.descripcion || ''
      });
    }
  }, [menu]);

  const hasOpData = opForm.principal.trim();

  const handleAddOpcion = () => {
    if (!hasOpData) return;
    setOpciones(prev => [...prev, { ...opForm, _key: Date.now() }]);
    setOpForm(prev => ({ ...EMPTY_OP, numero_opcion: prev.numero_opcion + 1 }));
    setShowOpForm(false);
  };

  const removeOpcion = (key) => setOpciones(prev => prev.filter(o => o._key !== key));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      if (isEditing) {
        await updateMenu(menu.id, form);
      } else {
        const data = await createMenu(form);
        const menuId = data.menu?.id;
        for (const op of opciones) {
          await addOpcion(menuId, {
            numero_opcion:  op.numero_opcion,
            principal:      op.principal      || null,
            proteina:       op.proteina       || null,
            acompanamiento1:op.acompanamiento1|| null,
            acompanamiento2:op.acompanamiento2|| null,
            ensalada:       op.ensalada       || null,
            bebida:         op.bebida         || null,
            postre:         op.postre         || null,
            precio:         op.precio         ? parseFloat(op.precio) : null,
            cupos_maximos:  op.cupos_maximos  ? parseInt(op.cupos_maximos) : null,
          });
        }
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow p-6">
      <h2 className="text-lg font-bold text-gray-800 mb-5">
        {isEditing ? 'Editar Menú' : 'Crear Nuevo Menú'}
      </h2>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Fecha *</label>
            <input type="date" value={form.fecha}
              onChange={e => setForm(f => ({ ...f, fecha: e.target.value }))}
              required min={!isEditing ? new Date().toISOString().split('T')[0] : undefined}
              className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Hora límite de selección *</label>
            <input type="time" value={form.hora_limite}
              onChange={e => setForm(f => ({ ...f, hora_limite: e.target.value }))}
              required className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Descripción (opcional)</label>
          <input type="text" value={form.descripcion}
            onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))}
            placeholder="Ej: Menú del lunes"
            className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
        </div>

        {/* Opciones inline (solo creación) */}
        {!isEditing && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-semibold text-gray-700">
                Opciones del menú
                <span className="ml-2 text-xs text-gray-400 font-normal">(cada opción es un menú completo)</span>
              </label>
              {!showOpForm && (
                <button type="button" onClick={() => setShowOpForm(true)}
                  className="flex items-center gap-1 px-3 py-1.5 bg-orange-100 text-orange-700 rounded-lg text-sm font-semibold hover:bg-orange-200">
                  <Plus className="w-3.5 h-3.5" /> Agregar opción
                </button>
              )}
            </div>

            {showOpForm && (
              <div className="bg-orange-50 rounded-xl p-4 mb-3 space-y-3 border border-orange-200">
                <div className="flex items-center gap-3 mb-1">
                  <span className="text-sm font-bold text-orange-700">Opción #{opForm.numero_opcion}</span>
                  <input type="number" value={opForm.numero_opcion} min="1" max="10"
                    onChange={e => setOpForm(f => ({ ...f, numero_opcion: parseInt(e.target.value) || 1 }))}
                    className="w-16 border border-gray-300 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {CAMPOS_OP.map(({ key, label, ph, required }) => (
                    <div key={key}>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">{label}{required && ' *'}</label>
                      <input type="text" value={opForm[key]}
                        onChange={e => setOpForm(f => ({ ...f, [key]: e.target.value }))}
                        placeholder={ph}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
                    </div>
                  ))}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">💰 Precio</label>
                      <input type="number" value={opForm.precio} step="0.01" min="0"
                        onChange={e => setOpForm(f => ({ ...f, precio: e.target.value }))}
                        placeholder="0.00"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">👥 Cupos máx.</label>
                      <input type="number" value={opForm.cupos_maximos} min="1"
                        onChange={e => setOpForm(f => ({ ...f, cupos_maximos: e.target.value }))}
                        placeholder="Sin límite"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" />
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 pt-1">
                  <button type="button" onClick={handleAddOpcion} disabled={!hasOpData}
                    className="flex items-center gap-1.5 px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-semibold hover:bg-orange-600 disabled:opacity-40">
                    <Plus className="w-3.5 h-3.5" /> Agregar
                  </button>
                  <button type="button" onClick={() => setShowOpForm(false)}
                    className="flex items-center gap-1.5 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-300">
                    <X className="w-3.5 h-3.5" /> Cancelar
                  </button>
                </div>
              </div>
            )}

            {opciones.length > 0 ? (
              <div className="space-y-2">
                {opciones.map((op) => (
                  <div key={op._key} className="flex items-start gap-3 bg-gray-50 rounded-xl px-4 py-3">
                    <span className="w-7 h-7 rounded-full bg-orange-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                      {op.numero_opcion}
                    </span>
                    <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-1 text-sm">
                      {op.principal     && <span><span className="text-gray-400 text-[10px] block">Principal</span>{op.principal}</span>}
                      {op.proteina      && <span><span className="text-gray-400 text-[10px] block">Proteína</span>{op.proteina}</span>}
                      {op.acompanamiento1 && <span><span className="text-gray-400 text-[10px] block">Acomp. 1</span>{op.acompanamiento1}</span>}
                      {op.acompanamiento2 && <span><span className="text-gray-400 text-[10px] block">Acomp. 2</span>{op.acompanamiento2}</span>}
                      {op.ensalada      && <span><span className="text-gray-400 text-[10px] block">Ensalada</span>{op.ensalada}</span>}
                      {op.bebida        && <span><span className="text-gray-400 text-[10px] block">Bebida</span>{op.bebida}</span>}
                      {op.postre        && <span><span className="text-gray-400 text-[10px] block">Postre</span>{op.postre}</span>}
                    </div>
                    <button type="button" onClick={() => removeOpcion(op._key)}
                      className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg flex-shrink-0">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 text-center py-3 bg-gray-50 rounded-xl">
                Sin opciones — puedes agregarlas ahora o después de crear el menú
              </p>
            )}
          </div>
        )}

        <div className="flex gap-3 pt-2">
          <button type="submit" disabled={saving}
            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50">
            <Save className="w-4 h-4" />
            {saving ? 'Guardando...' : isEditing ? 'Guardar cambios' : 'Crear menú'}
          </button>
          <button type="button" onClick={onCancel}
            className="flex items-center gap-2 px-5 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all">
            <X className="w-4 h-4" /> Cancelar
          </button>
        </div>
      </form>
    </div>
  );
}
